import { useToast } from '@chakra-ui/react';
import { ProductAccessRowStateType } from 'features/common/components/ProductAccessOptions/ProductAccessOptions';
import { useFormik } from 'formik';
import { useMemo, useState } from 'react';
import { useDrawerStateDispatch } from 'features/main/context';
import { closeDrawer } from 'features/main/context/drawer/actionCreators';
import {
  useUpdateOrganizationMemberRolesMutation,
  useGetOrganizationMemberQuery,
  GetOrganizationMemberDocument,
} from 'gql/graphql';
import { flatten } from 'lodash';
import { GroupDataApp } from 'features/user-group/user-group-list/components/groupListTableHelpers';

export const useAddAppsToMember = () => {
  const [productAccessState, setProductAccessState] =
    useState<ProductAccessRowStateType>([]);
  const [{ data: drawerData }, drawerDispatch] = useDrawerStateDispatch();
  const [updateOrganizationMemberRolesMutation, { loading }] =
    useUpdateOrganizationMemberRolesMutation();
  const toast = useToast();
  const { userId, existingApps } = drawerData || {};

  // Get current member data to merge with new apps
  const { data: memberData, refetch } = useGetOrganizationMemberQuery({
    variables: { id: userId || '' },
    skip: !userId,
  });

  const memberById = memberData?.organization?.memberById;

  // Transform existing apps to GroupDataApp format
  const existingTenants: GroupDataApp[] = useMemo(() => {
    const applications = memberById?.applications?.nodes ?? [];
    const appTenants = flatten(
      applications.map((app) =>
        (app.assignedRoles?.nodes || []).map((role) => ({
          tenantId: app.id,
          scope: role.productCode,
          role: role.role,
        })),
      ),
    );

    // Marketplace apps are handled separately, not included in tenantList
    return appTenants;
  }, [memberById?.applications?.nodes]);

  // Transform new apps from productAccessState to GroupDataApp format
  const newTenants: GroupDataApp[] = productAccessState.map((item) => ({
    tenantId: item.product.id,
    scope: item.product.productCode,
    role: item.accessType.value,
  }));

  // Use existingApps from drawer data if provided, otherwise use existingTenants from member query
  const tenantList = useMemo(() => {
    return existingApps || existingTenants;
  }, [existingApps, existingTenants]);

  // Formik setup
  const formik = useFormik({
    initialValues: {},
    onSubmit: async () => {
      if (!userId || newTenants.length === 0) {
        return;
      }

      try {
        // Prepare application roles to add
        // Filter out Marketplace apps and entries with null tenantId
        const applicationRolesToAdd = newTenants
          .filter((t) => t.scope !== 'Marketplace' && t.tenantId !== null)
          .map((t) => ({
            productCode: t.scope,
            applicationId: t.tenantId!, // Safe to use ! here since we filtered out nulls
            roleName: t.role,
          }));

        // Marketplace apps are not supported in this mutation
        // They would need to be added through a different API endpoint

        const { data } = await updateOrganizationMemberRolesMutation({
          variables: {
            userId,
            applicationRolesToAdd,
          },
          refetchQueries: [
            {
              query: GetOrganizationMemberDocument,
              variables: { id: userId },
            },
          ],
        });

        const error = data?.updateOrganizationMemberRoles.errors?.[0];
        if (error) {
          toast({
            title: 'Failed to add apps',
            description: error?.message || 'An error occurred',
            status: 'error',
          });
          return;
        }

        toast({
          title: 'Apps added successfully',
          description: 'The apps have been successfully added.',
          status: 'success',
        });

        await refetch();
        setProductAccessState([]);
        drawerDispatch(closeDrawer());
      } catch (error: any) {
        console.error('Error adding apps:', error);
        toast({
          title: 'Failed to add apps',
          description: error?.message || 'An error occurred while adding apps.',
          status: 'error',
        });
      }
    },
  });

  const handleCloseDrawer = () => {
    formik.resetForm();
    setProductAccessState([]);
    drawerDispatch(closeDrawer());
  };

  return {
    values: formik.values,
    errors: formik.errors,
    touched: formik.touched,
    handleChange: formik.handleChange,
    handleSubmit: formik.handleSubmit,
    handleBlur: formik.handleBlur,
    isSubmitting: formik.isSubmitting || loading,
    resetForm: formik.resetForm,
    productAccessState,
    setProductAccessState,
    handleCloseDrawer,
    existingApps: tenantList,
  };
};
